import { FilterStateItem, ConnectProto } from "@racoon/api";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { roleMap, RoleKey, User } from "~/shared/api/user/user.grpc";
import { Namespace, useT } from "@racoon/i18n";
import { NO_TEAM } from "~/shared/lib/driver-teams";

/** ---- UI state types ---- */
export type UserTabId =
  | "dispatchers"
  | "drivers"
  | "mechanics"
  | "loaders"
  | "support"
  | "management"
  | "archived"
  | "all";

/** ---- constants ---- */
export const getUserStates = (t: ReturnType<typeof useT>): FilterStateItem[] => [
  { key: t(Namespace.Employees, "tabs.dispatchers"), value: "dispatchers" },
  { key: t(Namespace.Employees, "tabs.drivers"), value: "drivers" },
  { key: t(Namespace.Employees, "tabs.mechanics"), value: "mechanics" },
  { key: t(Namespace.Employees, "tabs.loaders"), value: "loaders" },
  { key: t(Namespace.Employees, "tabs.support"), value: "support" },
  { key: t(Namespace.Employees, "tabs.management"), value: "management" },
  { key: t(Namespace.Employees, "tabs.archived"), value: "archived" },
  { key: t(Namespace.Employees, "tabs.all"), value: "all" },
];

export const stateToRole: Record<UserTabId, RoleKey | undefined> = {
  dispatchers: "USER_ROLE_PROVIDER_DISPATCHER",
  drivers: "USER_ROLE_DRIVER",
  mechanics: "USER_ROLE_PROVIDER_MECHANIC",
  loaders: "USER_ROLE_PROVIDER_LOADER",
  support: "USER_ROLE_SUPPORT",
  management: "USER_ROLE_PROVIDER_OWNER",
  archived: undefined, // special case
  all: undefined,
};

const S = (v: unknown) => (v == null ? "" : String(v));

/** ---- options ---- */
type Options = {
  /** Owner organization ID for API query */
  ownerOrgId?: number | null;
  /** Raw array returned by API (for client filtering) */
  data?: User[] | undefined;
  /** The organization's teams, every role (for the team filter) */
  teams?: ConnectProto.Team[];
};

export const useUsersUrlState = (opts: Options = {}) => {
  const [sp, setSp] = useSearchParams();
  const t = useT();

  // Generate translated states
  const USER_STATES = useMemo(() => getUserStates(t), [t]);

  /** ---------- BATCHED PATCHER (coalesces same-tick updates) ---------- */
  const pendingRef = useRef<URLSearchParams | null>(null);
  const flushIdRef = useRef<number | null>(null);

  const flushPatches = useCallback(() => {
    if (!pendingRef.current) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    flushIdRef.current = null;
    setSp(next, { replace: true });
  }, [setSp]);

  const patchMany = useCallback(
    (changes: Record<string, string | null>) => {
      const base = pendingRef.current ?? new URLSearchParams(sp);

      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === "") base.delete(k);
        else base.set(k, v);
      }

      pendingRef.current = base;

      if (flushIdRef.current == null) {
        flushIdRef.current = requestAnimationFrame(flushPatches);
      }
    },
    [sp, flushPatches],
  );

  // Cancel any pending flush on unmount so navigating away doesn't mutate the next page's URL.
  useEffect(() => {
    return () => {
      if (flushIdRef.current != null) {
        cancelAnimationFrame(flushIdRef.current);
        flushIdRef.current = null;
        pendingRef.current = null;
      }
    };
  }, []);

  const patch = useCallback((kv: Record<string, string | null>) => patchMany(kv), [patchMany]);

  // URL params (strings)
  const statusS = sp.get("status") ?? "drivers";
  const searchS = sp.get("search") ?? "";
  const teamS = sp.get("team") ?? "";

  const selectedState = USER_STATES.find((s) => s.value === statusS) ?? USER_STATES[1]; // drivers

  /** ----- URL-driven handlers ----- */
  // A team holds one role, so another tab's team would only empty the list.
  const onStateChange = (v: FilterStateItem) => patch({ status: v.value, team: null });
  const onFilterParamsChange = (key: string, value: string | null) => {
    if (key === "team") patch({ team: value || null });
  };
  const onSearchChange = (value: string) => patch({ search: value });

  // RTKQ query (stable primitives only)
  const apiQuery = useMemo<ConnectProto.UserListRequest | null>(() => {
    if (!opts.ownerOrgId) return null;

    const role = stateToRole[statusS as UserTabId];
    const isArchived = statusS === "archived";
    const isAll = statusS === "all";

    const params = {
      ownerOrganizationIds: [BigInt(opts.ownerOrgId)],
    } as ConnectProto.UserListRequest;

    // Role filter - convert string key to enum
    if (role) {
      params.roles = [roleMap[role]];
    }

    // Status filter
    if (isArchived) {
      params.statuses = [ConnectProto.UserStatus.INACTIVE];
    } else if (!isAll) {
      params.statuses = [ConnectProto.UserStatus.ACTIVE];
    }

    return params;
  }, [opts.ownerOrgId, statusS]);

  // FilterBlock params: the team filter offers the current tab's role only
  const filterParams = useMemo(() => {
    const role = stateToRole[statusS as UserTabId];
    const options = (opts.teams ?? [])
      .filter((team) => !role || team.userRole === roleMap[role])
      .map((team) => ({ id: String(team.id), name: team.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    options.push({ id: NO_TEAM, name: t(Namespace.Common, "noTeam") });
    return [
      {
        key: "team",
        type: "select" as const,
        searchable: true,
        label: t(Namespace.Employees, "filters.team"),
        options,
        value: teamS,
      },
    ];
  }, [opts.teams, statusS, teamS, t]);

  // Ids of the users the team filter lets through; null when it is off. Off, too,
  // until the teams load and for a team gone since the link was made: not an empty list.
  const teamUserIds = useMemo(() => {
    if (!teamS || !opts.teams) return null;
    const picked =
      teamS === NO_TEAM ? opts.teams : opts.teams.filter((team) => String(team.id) === teamS);
    if (teamS !== NO_TEAM && picked.length === 0) return null;
    return new Set(picked.flatMap((team) => team.users.map((u) => String(u.id))));
  }, [opts.teams, teamS]);

  // client-side search filtering + totals
  const { items, total, filteredTotal } = useMemo(() => {
    const all = opts.data ?? [];
    const totalAll = all.length;

    const matches = (user: User) => {
      if (teamUserIds) {
        // "No team" lets through those in no team; a team, its members.
        if (teamUserIds.has(String(user.id)) === (teamS === NO_TEAM)) return false;
      }

      // Search by name, phone, email, driver license number
      if (searchS) {
        const searchLower = searchS.toLowerCase();
        const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").toLowerCase();
        const phone = S(user.phoneNumber).toLowerCase();
        const email = S(user.email).toLowerCase();
        const driverLicense = S(user.driverLicenseNumber).toLowerCase();

        const matchesSearch =
          fullName.includes(searchLower) ||
          phone.includes(searchLower) ||
          email.includes(searchLower) ||
          driverLicense.includes(searchLower);

        if (!matchesSearch) return false;
      }

      return true;
    };

    const filtered = all.filter(matches);
    return { items: filtered, total: totalAll, filteredTotal: filtered.length };
  }, [opts.data, searchS, teamUserIds, teamS]);

  return {
    // url-driven state
    states: USER_STATES,
    selectedState,
    onStateChange,
    onSearchChange,
    searchValue: searchS,

    // API query
    apiQuery,

    // FilterBlock params
    filterParams,
    onFilterParamsChange,

    // Filtered data
    items,
    total,
    filteredTotal,
  };
};
